import React, { useState } from "react";
import {
  Table,
  TableRow,
  TableCell,
  TableHead,
  TableBody,
  Typography,
  TablePagination,
  Chip,
  Box,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  TableContainer,
  Paper,
  IconButton,
  Tooltip,
} from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import List from "@mui/material/List";
import ListItem from "@mui/material/ListItem";
import ListItemText from "@mui/material/ListItemText";
import Divider from "@mui/material/Divider";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import { useEffectAsync } from "../reactHelper";
import { getWithExpiry } from "../common/util/localstorage";
import SearchHeader, { filterByKeyword } from "../common/components/SearchHeader";
import CollectionActions from "../common/components/CollectionActions";
import { productsActions, maintenancesActions } from "../store";
import TableShimmer from "../common/components/TableShimmer";
import { useDispatch, useSelector } from "react-redux";
import CollectionFab from "../settings/CollectionFab";
import { useNavigate } from "react-router-dom";
import ErrorDialog from "../common/components/ErrorDialog";

/* -------------------- STATUT COLORS -------------------- */

const statutColors = {
  "en attente": "#FFA500",
  "en cours de reparation": "#1E90FF",
  reparer: "#32CD32",
  retour: "#800080",
  livré: "#008000",
  "non réparable": "#FF0000",
  "-": "#ccc",
};

/* -------------------- COMPONENT -------------------- */

const ProductsPage = () => {
  const [loading, setLoading] = useState(false);
  const [searchKeyword, setSearchKeyword] = useState("");
  const [opening, setOpening] = useState(false);
  const [errorMsg, setErrorMsg] = useState("Error !!");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [filterType, setFilterType] = useState("all");
  const [filterStatut, setFilterStatut] = useState("all");
  const [historyOpen, setHistoryOpen] = useState(false);
const [historyData, setHistoryData] = useState([]);
const [historyLoading, setHistoryLoading] = useState(false);
  const _products = useSelector((state) => state.products.items);
  const _maintenances = useSelector((state) => state.maintenances.items);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  /* -------------------- FETCH DATA -------------------- */

  useEffectAsync(async () => {
    setLoading(true);
    try {
      const resProducts = await fetch(
        `${process.env.REACT_APP_URL_NAME}/products`,
        {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            Authorization: `Bearer ${getWithExpiry("TOKEN")}`,
          },
        }
      );

      if (!resProducts.ok) {
        if (resProducts.status === 401) {
          setErrorMsg("UNAUTHORIZED");
          setOpening(true);
          navigate("/login");
        } else {
          setErrorMsg(await resProducts.text());
          setOpening(true);
        }
        return;
      }

      const productsData = await resProducts.json();
      dispatch(productsActions.setItems(productsData));

      const allMaintenances = [];
      await Promise.all(
        productsData.map(async (p) => {
          const res = await fetch(
            `${process.env.REACT_APP_URL_NAME}/maintenances/produit/${p._id}`,
            {
              headers: {
                Accept: "application/json",
                "Content-Type": "application/json",
                Authorization: `Bearer ${getWithExpiry("TOKEN")}`,
              },
            }
          );
          if (res.ok) {
            const data = await res.json();
            allMaintenances.push(...data.maintenances);
          }
        })
      );

      dispatch(maintenancesActions.setItems(allMaintenances));
    } finally {
      setLoading(false);
    }
  }, []);

  const handleOpeningResult = () => setOpening(false);

  /* -------------------- HELPERS -------------------- */

  const getLatestStatut = (productId) => {
    const maints = _maintenances
      .filter((m) => m.produit?._id === productId)
      .sort(
        (a, b) =>
          new Date(b.datetime_debut) - new Date(a.datetime_debut)
      );
    return maints.length ? maints[0].statut : "-";
  };

  const types = ["all", ...new Set(_products.map((p) => p.type))];
  const statuts = ["all", ...new Set(_maintenances.map((m) => m.statut))];

  const filteredProducts = _products
    .filter(filterByKeyword(searchKeyword))
    .filter((p) => (filterType === "all" ? true : p.type === filterType))
    .filter((p) =>
      filterStatut === "all"
        ? true
        : getLatestStatut(p._id) === filterStatut
    );

  const paginatedProducts = filteredProducts.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  /* -------------------- PDF EXPORT -------------------- */

  const exportPDF = () => {
    const doc = new jsPDF("landscape");

    doc.setFontSize(14);
    doc.text("Rapport des Produits", 14, 15);

    doc.setFontSize(10);
    doc.text(
      `Type: ${filterType === "all" ? "Tous" : filterType} | Statut maintenance: ${
        filterStatut === "all" ? "Tous" : filterStatut
      } | Recherche: ${searchKeyword || "-"}`,
      14,
      22
    );

    autoTable(doc, {
      startY: 28,
      head: [[
        "#",
        "Référence",
        "Désignation",
        "Marque",
        "Modèle",
        "Type",
        "Client",
        "Date entrée",
        "Statut produit",
        "Statut maintenance",
        "Ajouté par",
        "Livré par",
        "Commentaire",
      ]],
      body: filteredProducts.map((p, i) => [
        i + 1,
        p.reference || "-",
        p.designation || "-",
        p.marque || "-",
        p.model || "-",
        p.type || "-",
        p.client?.username || "-",
        p.date_entree
          ? new Date(p.date_entree).toLocaleDateString()
          : "-",
        p.statut || "-",
        getLatestStatut(p._id),
        p.ajouté_par || "-",
        p.livré_par || "-",
        p.commentaire || "-",
      ]),
      styles: { fontSize: 8 },
      headStyles: { fillColor: [0, 35, 85] },
    });

    doc.save("produits.pdf");
  };

  /* -------------------- RENDER -------------------- */
const getLatestMaintenance = (productId) => {
  const maints = _maintenances
    .filter((m) => m.produit?._id === productId)
    .sort(
      (a, b) =>
        new Date(b.datetime_debut) - new Date(a.datetime_debut)
    );

  return maints.length ? maints[0] : null;
};


const openHistory = async (productId) => {
  const latest = getLatestMaintenance(productId);

  if (!latest) return;

  setHistoryOpen(true);
  setHistoryLoading(true);

  try {
    const res = await fetch(
      `${process.env.REACT_APP_URL_NAME}/maintenances/${latest._id}/history`,
      {
        headers: {
          Authorization: `Bearer ${getWithExpiry("TOKEN")}`,
        },
      }
    );

    const data = await res.json();
    setHistoryData(data);
  } finally {
    setHistoryLoading(false);
  }
};
  return (
    <>
      <Typography
        variant="h3"
        align="center"
        sx={{
          mb: 3,
          fontWeight: 800,
          background: "linear-gradient(90deg, #002355, #0081f9)",
          WebkitBackgroundClip: "text",
          WebkitTextFillColor: "transparent",
        }}
      >
        Produits
      </Typography>

      <Box
        display="flex"
        flexDirection={{ xs: "column", sm: "row" }}
        justifyContent="space-between"
        alignItems="center"
        mb={2}
        gap={2}
      >
        <SearchHeader
          keyword={searchKeyword}
          setKeyword={setSearchKeyword}
        />

        <Box display="flex" gap={2}>
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Type</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="Type"
            >
              {types.map((t) => (
                <MenuItem key={t} value={t}>
                  {t}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>Statut</InputLabel>
            <Select
              value={filterStatut}
              onChange={(e) => setFilterStatut(e.target.value)}
              label="Statut"
            >
              {statuts.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Tooltip title="Exporter en PDF">
            <IconButton
              onClick={exportPDF}
              sx={{
                background:
                  "linear-gradient(135deg,#d32f2f,#ff5252)",
                color: "#fff",
                width: 44,
                height: 44,
                "&:hover": {
                  background:
                    "linear-gradient(135deg,#b71c1c,#ff1744)",
                },
              }}
            >
              <PictureAsPdfIcon />
            </IconButton>
          </Tooltip>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ boxShadow: 3 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: "#002355" }}>
              {[
                "#",
                "REFERENCE",
                "DESIGNATION",
                "MARQUE",
                "MODEL",
                "TYPE",
                "CLIENT",
                "DATE D'ENTRÉE",
                "COMMENTAIRE",
                "AJOUTÉ PAR",
                "MODIFIÉ PAR",
                "STATUT PRODUIT",
                "LIVRÉ PAR",
                "STATUT MAINTENANCE",
                "MAINT HISTORY",
                "ACTIONS",
              ].map((h) => (
                <TableCell
                  key={h}
                  sx={{ color: "#fff", fontWeight: 700 }}
                >
                  {h}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>

          <TableBody>
            {!loading && paginatedProducts.length ? (
              paginatedProducts.map((p, i) => (
                <TableRow key={p._id} hover>
                  <TableCell>
                    {page * rowsPerPage + i + 1}
                  </TableCell>
                  <TableCell>{p.reference}</TableCell>
                  <TableCell>{p.designation}</TableCell>
                  <TableCell>{p.marque}</TableCell>
                  <TableCell>{p.model}</TableCell>
                  <TableCell>{p.type}</TableCell>
                  <TableCell>
                    {p.client?.username || "-"}
                  </TableCell>
                  <TableCell>
                    {p.date_entree
                      ? new Date(
                          p.date_entree
                        ).toLocaleDateString()
                      : "-"}
                  </TableCell>
                  <TableCell>{p.commentaire || "-"}</TableCell>
                  <TableCell>{p.ajouté_par || "-"}</TableCell>
                  <TableCell>{p.modifier_par || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={p.statut || "-"}
                      size="small"
                      sx={{
                        color: "#fff",
                        backgroundColor:
                          statutColors[p.statut] || "#ccc",
                      }}
                    />
                  </TableCell>
                  <TableCell>{p.livré_par || "-"}</TableCell>
                  <TableCell>
                    <Chip
                      label={getLatestStatut(p._id)}
                      size="small"
                      sx={{
                        color: "#fff",
                        backgroundColor:
                          statutColors[
                            getLatestStatut(p._id)
                          ] || "#ccc",
                      }}
                    />
                  </TableCell>
                  <TableCell>
  <Tooltip title="Historique maintenance">
    <IconButton
      color="info"
      onClick={() => openHistory(p._id)}
    >
      <HistoryIcon />
    </IconButton>
  </Tooltip>
</TableCell>
                  <TableCell padding="none">
                    <CollectionActions
                      itemId={p._id}
                      editPath="/settings/product"
                      endpoint="products"
                      action={productsActions}
                      removable
                    />
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableShimmer columns={15} endAction />
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={filteredProducts.length}
        page={page}
        onPageChange={(e, p) => setPage(p)}
        rowsPerPage={rowsPerPage}
        onRowsPerPageChange={(e) => {
          setRowsPerPage(+e.target.value);
          setPage(0);
        }}
      />

      <CollectionFab editPath="/settings/product" />
<Dialog
  open={historyOpen}
  onClose={() => setHistoryOpen(false)}
  fullWidth
  maxWidth="md"
>
  <DialogTitle>
    Historique maintenance
  </DialogTitle>

  <DialogContent>
    {historyLoading ? (
      "Loading..."
    ) : historyData.length === 0 ? (
      "Aucun historique"
    ) : (
      <List>
        {historyData.map((h, i) => (
          <div key={h._id}>
            <ListItem>
              <ListItemText
                primary={`${h.old_statut} → ${h.new_statut}`}
                secondary={`👤 ${h.changedBy} | 📅 ${new Date(
                  h.createdAt
                ).toLocaleString()}`}
              />
            </ListItem>

            {i !== historyData.length - 1 && <Divider />}
          </div>
        ))}
      </List>
    )}
  </DialogContent>
</Dialog>
      <ErrorDialog
        open={opening}
        errorMsg={errorMsg}
        onResult={handleOpeningResult}
      />
    </>
  );
};

export default ProductsPage;
